import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FaPaperPlane } from "react-icons/fa";

interface IMessage {
  text: string;
  sender: "user" | "bot";
}

const ChatBot: React.FC = () => {
  const [messages, setMessages] = useState<IMessage[]>([
    { text: "Olá! Como posso te ajudar?", sender: "bot" }
  ]);
  const [input, setInput] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (input.trim() === "") return;

    // Adiciona a mensagem do usuário
    const userMessage: IMessage = { text: input, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    // Simula o bot digitando
    setIsBotTyping(true);
    const typingMessage: IMessage = { text: "digitando...", sender: "bot" };
    setMessages((prev) => [...prev, typingMessage]);

    // Aguarda um tempo para simular o processamento
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Remove o indicador de "digitando..."
    setMessages((prev) => {
      // Se a última mensagem for o "digitando...", remove-a
      if (prev[prev.length - 1].text === "digitando...") {
        return prev.slice(0, prev.length - 1);
      }
      return prev;
    });
    setIsBotTyping(false);

    // Gera a resposta do bot com base na mensagem do usuário
    const botReply: IMessage = { text: generateBotReply(userMessage.text), sender: "bot" };
    setMessages((prev) => [...prev, botReply]);
  };

  const generateBotReply = (userText: string): string => {
    const text = userText.toLowerCase();
    if (text.includes("oi") || text.includes("olá")) {
      return "Olá! Como posso ajudar você hoje?";
    } else if (text.includes("como você está")) {
      return "Estou bem, obrigado por perguntar!";
    } else if (text.includes("hora")) {
      return `Agora são ${new Date().toLocaleTimeString()}.`;
    } else if (text.includes("o que voce é?") || text.includes("o que você é?") ){
      return "Sou programador Frontend!";
    } else if (text.includes("qual sua idade?") || text.includes("quantos anos voce tem? ") || text.includes("quantos anos você tem? ")){
      return "Tenho 22 anos";
    }
    else {
      return "Estou aprendendo! Logo serei mais inteligente!";
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <Container>
      <Title>ChatBot Assistente</Title>
      <ChatWindow ref={chatRef}>
        {messages.map((msg, index) => (
          <Message key={index} sender={msg.sender}>
            {msg.text}
          </Message>
        ))}
      </ChatWindow>
      <InputContainer>
        <Input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Digite sua pergunta..."
          disabled={isBotTyping}
        />
        <SendButton onClick={handleSendMessage} disabled={isBotTyping}>
          <FaPaperPlane size={16} />
        </SendButton>
      </InputContainer>
    </Container>
  );
};

export default ChatBot;

/* Estilos */

const Container = styled.div`
  padding: 4rem 2rem;
  min-height: 100vh;
  background: #f8f9fa;
  font-family: "Segoe UI", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #2a5298;
  text-align: center;
  margin-bottom: 2rem;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: #ffa726;
  }
`;

const ChatWindow = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: white;
  border-radius: 10px;
  padding: 1rem;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: #6a80e5 #eee;
`;

const Message = styled.p<{ sender: string }>`
  margin: 0.5rem 0;
  padding: 0.8rem;
  font-size: 1rem;
  border-radius: 8px;
  max-width: 75%;
  align-self: ${(props) => (props.sender === "user" ? "flex-end" : "flex-start")};
  background: ${(props) => (props.sender === "user" ? "#2a5298" : "#ddd")};
  color: ${(props) => (props.sender === "user" ? "white" : "#333")};
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  max-width: 500px;
  margin-top: 1rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.5rem;
  font-size: 1rem;
  border: 1px solid #555;
  border-radius: 6px;
  background: white;
  color: #222;
  outline: none;

  &:focus {
    border-color: #6a80e5;
    box-shadow: 0 0 5px rgba(106, 128, 229, 0.5);
  }
`;

const SendButton = styled.button`
  padding: 0.5rem 1rem;
  font-size: 1rem;
  color: white;
  background: #2a5298;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #1e3c72;
  }
`;
